import { useMemo } from 'react';
import type { RemittanceRole } from '@capture/shared';
import { fieldContentName } from '@capture/shared';
import { useFormConfigStore, resolveV3, activeForm as resolveActiveForm } from '../state/store';
import { useRemittanceIssues } from './useRemittanceIssues';
import './FormRemittancePanel.css';

export interface FormRemittancePanelProps {
  /**
   * The form currently in scope — mirrors `FieldListPane`'s `activeFormId`
   * prop (`FormConfigPage`'s resolved `?form=` id). `null` resolves to the
   * default form via the same fallback `activeForm` uses everywhere else.
   */
  activeFormId: string | null;
}

const ROLE_OPTIONS: { value: RemittanceRole; label: string }[] = [
  { value: 'coupon', label: 'Coupon' },
  { value: 'payment', label: 'Payment' },
  { value: 'attachment', label: 'Attachment' },
];

interface FieldSelectProps {
  id: string;
  label: string;
  value: string;
  options: string[];
  onChange: (value: string) => void;
}

/** Shared shape for the amountField / appliedAmountField pickers below —
 *  both are "pick one of this form's fields" selects, differing only in id,
 *  label, current value, and where the chosen name gets written. */
function FieldSelect({ id, label, value, options, onChange }: FieldSelectProps) {
  return (
    <label className="form-remittance-panel__field" htmlFor={id}>
      {label}
      <select id={id} value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="" disabled>
          Select a field
        </option>
        {options.map((name) => (
          <option key={name} value={name}>
            {name}
          </option>
        ))}
      </select>
    </label>
  );
}

/**
 * SP6 Task 8 — per-form remittance panel on the Fields tab. Mounted by
 * FormConfigPage ONLY when the job's surface is 'remittance' (the SP1 tab
 * gate); this component does no surface check of its own — see
 * `FormConfigPage.tsx`'s `jobType?.surface` gate around
 * `form-config-page__fields-tab`.
 *
 * Reads mirror `FieldListPane`: resolve the V3 blob via `resolveV3` (so
 * test/pre-load paths seeding only the derived `content` still work) and the
 * active form via `activeForm`, using the SAME `activeFormId` prop the page
 * threads to every Fields-tab child. Field-option and amount/instrument
 * writes go through `updateActiveForm`, which targets the STORE's
 * `activeFormId` — kept in sync with this prop by FormConfigPage's own
 * effect, exactly like every other Fields-tab writer.
 *
 * The role -> dependent-key invariant (an `instrument` is only valid for
 * role `'payment'`; `appliedAmountField` only for `'coupon'`) is NOT
 * hand-rolled here — it lives in the store's `setFormRemittanceRole`, so any
 * future caller that changes a form's role (bulk edit, import, clone) gets
 * the same guarantee this panel does: the draft is never observed with a
 * role and a now-invalid dependent key both set.
 */
export function FormRemittancePanel({ activeFormId }: FormRemittancePanelProps) {
  const contentV3 = useFormConfigStore((s) => s.contentV3);
  const legacyContent = useFormConfigStore((s) => s.content);
  const updateActiveForm = useFormConfigStore((s) => s.updateActiveForm);
  const setFormRemittanceRole = useFormConfigStore((s) => s.setFormRemittanceRole);
  const issueBuckets = useRemittanceIssues();

  const v3 = useMemo(
    () => resolveV3({ contentV3, content: legacyContent }),
    [contentV3, legacyContent],
  );
  const form = useMemo(() => (v3 ? resolveActiveForm(v3, activeFormId) : null), [v3, activeFormId]);
  const fieldOptions = useMemo(() => (form ? form.fields.map(fieldContentName) : []), [form]);
  const issues = useMemo(
    () => (form ? issueBuckets.forms.filter((issue) => issue.formId === form.id) : []),
    [issueBuckets.forms, form],
  );

  if (!v3 || !form) return null;
  const role = form.remittanceRole;

  return (
    <section className="form-remittance-panel">
      <h3 className="form-remittance-panel__heading">Remittance</h3>

      {issues.length > 0 && (
        <ul className="form-remittance-panel__issues" role="alert">
          {issues.map((issue, i) => (
            <li key={`${issue.code}-${i}`}>{issue.message}</li>
          ))}
        </ul>
      )}

      <label className="form-remittance-panel__field" htmlFor="form-remittance-role">
        Remittance role
        <select
          id="form-remittance-role"
          value={role ?? ''}
          onChange={(e) => {
            const next = e.target.value as RemittanceRole | '';
            setFormRemittanceRole(next === '' ? undefined : next);
          }}
        >
          <option value="">— none —</option>
          {ROLE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </label>

      {role === 'payment' && (
        <div className="form-remittance-panel__instrument">
          <p className="form-remittance-panel__instrument-type">Instrument: Check</p>
          <FieldSelect
            id="form-remittance-amount-field"
            label="Amount field"
            value={form.instrument?.amountField ?? ''}
            options={fieldOptions}
            onChange={(value) => updateActiveForm({ instrument: { type: 'check', amountField: value } })}
          />
        </div>
      )}

      {role === 'coupon' && (
        <FieldSelect
          id="form-remittance-applied-amount-field"
          label="Applied amount field"
          value={form.appliedAmountField ?? ''}
          options={fieldOptions}
          onChange={(value) => updateActiveForm({ appliedAmountField: value })}
        />
      )}
    </section>
  );
}
